
import { logger } from '@/services/LoggingService';
import { extractConnectionDetailsFromUrl } from './utils/redirect';
import { VEZGO_REGISTERED_REDIRECT_URIS } from './constants';

/**
 * Initialize callback detection for Vezgo flows
 * This runs immediately upon import to detect and handle callbacks as early as possible
 */
function initVezgoCallbackDetection() {
  try {
    // Check if we're on a registered callback path
    const isRegisteredPath = VEZGO_REGISTERED_REDIRECT_URIS.some(uri => {
      const registeredPath = new URL(uri).pathname;
      return window.location.pathname.includes(registeredPath);
    });
    
    // Also check for legacy callback paths for backward compatibility
    const isLegacyPath = window.location.pathname.includes('/vezgo-callback') || 
                          window.location.pathname.includes('/callback/vezgo') ||
                          window.location.pathname.includes('/wealthica-redirect');
    
    // Extract connection details from URL if available
    const connectionDetails = extractConnectionDetailsFromUrl();
    
    if (connectionDetails || isRegisteredPath || isLegacyPath) {
      logger.info('vezgo.initCallbacks', 'Detected callback URL', {
        path: window.location.pathname,
        isRegisteredPath,
        isLegacyPath,
        account: connectionDetails?.account, // Changed from provider to account
        search: window.location.search
      });
      
      // Check if we've processed this callback already
      const callbackProcessed = sessionStorage.getItem('vezgo_callback_processed') === 'true';
      
      if (callbackProcessed) {
        const processTime = sessionStorage.getItem('vezgo_callback_time');
        const timeElapsed = processTime ? Date.now() - parseInt(processTime, 10) : 0;
        
        logger.info('vezgo.initCallbacks', 'Callback already processed', {
          timeElapsed,
          processTime
        });
        
        // Clear processed flag after 5 minutes to prevent stale state
        if (timeElapsed > 5 * 60 * 1000) {
          sessionStorage.removeItem('vezgo_callback_processed');
          sessionStorage.removeItem('vezgo_callback_time');
        }
      } else {
        // Store connection details in session storage for retrieval in app
        if (connectionDetails) {
          // Store account ID instead of provider in session storage
          sessionStorage.setItem('vezgo_callback_account', connectionDetails.account || '');
        }
        
        logger.info('vezgo.initCallbacks', 'Stored callback details in session storage');
      }
    }
  } catch (err) {
    logger.error('vezgo.initCallbacks', 'Error in callback detection', {
      error: err instanceof Error ? err.message : String(err),
      path: window.location.pathname,
      search: window.location.search
    });
  }
}

// Execute initialization immediately 
initVezgoCallbackDetection();

// Export a function to check if we're handling a callback
export function isHandlingCallback(): boolean {
  // Check for registered callback paths
  const isRegisteredPath = VEZGO_REGISTERED_REDIRECT_URIS.some(uri => {
    const registeredPath = new URL(uri).pathname;
    return window.location.pathname.includes(registeredPath);
  });
  
  // Also check for legacy callback paths
  const isLegacyPath = window.location.pathname.includes('/vezgo-callback') ||
                        window.location.pathname.includes('/callback/vezgo') ||
                        window.location.pathname.includes('/wealthica-redirect');
  
  // Check for connection details in URL parameters
  const hasConnectionDetails = !!extractConnectionDetailsFromUrl();
  
  return isRegisteredPath || isLegacyPath || hasConnectionDetails;
}

// Export function to get cached callback details
export function getCachedCallbackDetails() {
  return {
    account: sessionStorage.getItem('vezgo_callback_account') || undefined, // Changed from provider to account
    processed: sessionStorage.getItem('vezgo_callback_processed') === 'true'
  };
}

// Export functions to mark callback as processed
export function markCallbackProcessed() {
  sessionStorage.setItem('vezgo_callback_processed', 'true');
  sessionStorage.setItem('vezgo_callback_time', Date.now().toString());
}
